import React, { useState, useEffect } from 'react';
import { orderBy } from 'lodash';
import { Box, Button, DropButton, Text, Tip } from 'grommet';
import { CircleInformation, More } from 'grommet-icons';
import SimpleTable from './simple-table';
import { capitalize, get } from 'lodash';
import { canCreate } from '../auth';

const ChildGrid = ({
  columns,
  items,
  newLabel,
  noLabel,
  header,
  onNewClick,
  onItemDoubleClick = () => {},
  keyBuilder = r => r.id,
  defaultSortField = 'name',
  defaultSortDirection = 'asc',
  itemType,
  tablebgColor = 'white400',
  additionalControls = () => null
}) => {
  const [selectedItem, setSelectedItem] = useState();

  // Get initial sort state from localStorage
  const getInitialSortState = () => {
    const savedSort = localStorage.getItem(`simpleTableSort_${itemType}`);
    if (savedSort) {
      try {
        return JSON.parse(savedSort);
      } catch (e) {
        console.warn('Error parsing saved sort state:', e);
      }
    }
    return {
      field: defaultSortField,
      direction: defaultSortDirection
    };
  };

  const [columnSort, setColumnSort] = useState(getInitialSortState());
  const [sortedItems, setSortedItems] = useState([]);

  // Save sort state to localStorage when it changes
  useEffect(() => {
    localStorage.setItem(
      `simpleTableSort_${itemType}`,
      JSON.stringify(columnSort)
    );
  }, [columnSort, itemType]);

  useEffect(() => {
    setSortedItems(
      orderBy(
        items,
        row => {
          if (typeof columnSort.field === 'function') {
            const value = columnSort.field(row);
            return value.toLowerCase();
          } else {
            const value = get(row, columnSort.field, '');
            if (typeof value === 'string') {
              return value.toLowerCase();
            }
            return value;
          }
        },
        [columnSort.direction]
      )
    );
  }, [items, columnSort]);

  return (
    <Box flex={true} fill>
      <Box
        direction='row'
        justify={header ? 'between' : 'end'}
        pad={{ vertical: 'small' }}
        align='center'
      >
        {header && <Text weight={'bold'}>{header}</Text>}
        <Box direction='row' gap='small'>
          {additionalControls && additionalControls()}
          {canCreate(itemType) && (
            <Button label={newLabel} onClick={onNewClick} primary />
          )}
        </Box>
      </Box>
      <Box flex={true} fill>
        <SimpleTable
          tablebgColor={tablebgColor}
          columns={columns}
          data={sortedItems}
          searchFilter={columnSort.field}
          searchOrder={columnSort.direction}
          onHeaderClick={c => {
            if (c.sort) {
              const newColumnSort = {};
              if (c.sort === columnSort.field) {
                newColumnSort.field = columnSort.field;

                newColumnSort.direction =
                  columnSort.direction === 'desc' ? 'asc' : 'desc';
              } else {
                newColumnSort.field = c.sort;
                newColumnSort.direction = c.defaultOrder;
              }
              setColumnSort(newColumnSort);
            }
          }}
          noItemsText={noLabel}
          selectedItem={selectedItem}
          onSelectItem={setSelectedItem}
          keyBuilder={keyBuilder}
          onItemDoubleClick={onItemDoubleClick}
          entityType={itemType}
        />
      </Box>
    </Box>
  );
};

ChildGrid.displayName = 'ChildGrid';

export default ChildGrid;

/*
  type ExtraOptions = {
    label: string,
    icon?: JSX.Element,
    action: (e: React.MouseEvent) => any
  }
*/
export const MoreColumn = ({
  onEdit,
  editLabel = 'Edit',
  status,
  onStatusChange,
  entity,
  canChangeStatus = true,
  onDuplicate,
  extraOptions // ExtraOptions[]?
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const hasActions =
    typeof onEdit === 'function' ||
    canChangeStatus ||
    typeof onDuplicate === 'function';
  return (
    <Box fill align='center' justify='center'>
      <DropButton
        plain
        icon={<More />}
        open={isOpen}
        onClick={() => setIsOpen(!isOpen)}
        dropProps={{ onClickOutside: () => setIsOpen(false) }}
        dropAlign={{ top: 'bottom', right: 'right' }}
        dropContent={
          <Box>
            {typeof onEdit === 'function' && (
              <Button
                plain
                hoverIndicator={true}
                onClick={() => {
                  onEdit(entity);
                  setIsOpen(false);
                }}
              >
                <Box width='small' pad='small'>
                  <Text>{editLabel}</Text>
                </Box>
              </Button>
            )}
            {canChangeStatus && (
              <Button
                plain
                hoverIndicator={true}
                onClick={() => {
                  onStatusChange(
                    entity,
                    status === 'Active' ? 'Inactive' : 'Active'
                  );
                  setIsOpen(false);
                }}
              >
                <Box width='small' pad='small'>
                  <Text>{status === 'Active' ? 'Deactivate' : 'Activate'}</Text>
                </Box>
              </Button>
            )}
            {typeof onDuplicate === 'function' && (
              <Button
                plain
                hoverIndicator={true}
                onClick={() => {
                  onDuplicate(entity);
                  setIsOpen(false);
                }}
              >
                <Box width='small' pad='small'>
                  <Text>Duplicate</Text>
                </Box>
              </Button>
            )}
            {extraOptions?.length ? (
              <>
                {extraOptions.map(option => (
                  <Button
                    key={option.label}
                    plain
                    hoverIndicator
                    onClick={e => {
                      option.action(e, entity);
                      setIsOpen(false);
                    }}
                  >
                    <Box
                      width='small'
                      pad='small'
                      direction='row'
                      align='center'
                      gap='xsmall'
                    >
                      {option.icon && <>{option.icon}</>}
                      <Text>{option.label}</Text>
                    </Box>
                  </Button>
                ))}
              </>
            ) : null}
            {!hasActions && (
              <Button
                plain
                hoverIndicator={true}
                onClick={() => {
                  setIsOpen(false);
                }}
              >
                <Box width='small' pad='small'>
                  <Text style={{ fontStyle: 'italic' }}>
                    No Actions Available
                  </Text>
                </Box>
              </Button>
            )}
          </Box>
        }
      />
    </Box>
  );
};

const getStatusColor = status => {
  const lowerStatus = status?.toLowerCase();
  if (
    status === 'Active' ||
    lowerStatus === 'allowed' ||
    lowerStatus === 'completed'
  ) {
    return 'status-ok';
  } else if (
    status === 'Inactive' ||
    lowerStatus === 'blocked' ||
    lowerStatus === 'failed'
  ) {
    return 'status-critical';
  } else if (lowerStatus === 'pending') {
    return 'status-warning';
  } else if (lowerStatus === 'upcoming') {
    return 'brand500';
  } else if (lowerStatus === 'canceled') {
    return 'status-unknown';
  } else {
    return 'status-warning';
  }
};

export const StatusIcon = ({
  status,
  count = 0,
  inactive = false,
  active = false,
  label,
  disabled = false,
  tooltip
}) => {
  const displayText =
    label ||
    (status
      ? count
        ? `${count} ${capitalize(status)}`
        : capitalize(status)
      : 'Draft');

  const getBackground = () => {
    if (active) return 'black';
    if (inactive) return 'white';
    return getStatusColor(status);
  };

  const getTextColor = () => {
    if (active) return 'white';
    if (inactive) return 'text';
    return getStatusColor(status);
  };

  return (
    <Box
      style={{ position: 'relative', opacity: disabled ? 0.2 : 1 }}
      flex={true}
    >
      <Box
        round='medium'
        fill
        background={getBackground()}
        align='center'
        justify='center'
        style={{ opacity: inactive || active ? 1 : 0.2 }}
        border={inactive ? { color: 'border400' } : undefined}
      ></Box>
      <Box
        style={{ position: 'absolute', left: 0, right: 0, top: 0, bottom: 0 }}
        align='center'
        justify='center'
      >
        <Box direction='row' align='center' gap='xsmall'>
          <Text weight='bold' color={getTextColor()}>
            {displayText}
          </Text>
          {tooltip && (
            <Tip content={tooltip}>
              <CircleInformation
                size='small'
                color={getTextColor()}
                a11yTitle={tooltip}
              />
            </Tip>
          )}
        </Box>
      </Box>
    </Box>
  );
};

// Standalone status badge with proper dimensions for use outside of tables
export const StatusBadge = ({
  status,
  count,
  width = '80px',
  height = '32px',
  onClick,
  label,
  inactive = false,
  active = false,
  highlighted = false
}) => {
  // Only apply disabled state to action buttons (those with labels like "Allow", "Block")
  // Don't apply to status display badges (those without onClick or with count)
  // highlighted=true forces the badge to show at full opacity (used for ad-unit level status)
  const isActionButton = label && !count;
  const shouldShowDisabled = isActionButton && !onClick && !highlighted;

  return (
    <Box
      width={count ? '105px' : width}
      height={height}
      onClick={onClick}
      style={onClick ? { cursor: 'pointer', outline: 'none' } : undefined}
      focusIndicator={false}
    >
      <StatusIcon
        status={status}
        count={count}
        inactive={inactive}
        active={active}
        label={label}
        disabled={shouldShowDisabled}
      />
    </Box>
  );
};
